import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ARTICLES } from "@/pages/blog/articles";

export default function BlogArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) return;
    document.title = `${article.title} | Zuntria`;
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.metaDesc,
        datePublished: article.date,
        author: { "@type": "Organization", name: "Zuntria" },
        publisher: { "@type": "Organization", name: "Zuntria Limited", url: "https://zuntria.co.uk" },
        mainEntityOfPage: `https://zuntria.co.uk/blog/${article.slug}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (article.faq || []).map((f) => ({
          "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "article-jsonld";
    el.textContent = JSON.stringify(schemas);
    document.head.appendChild(el);
    let meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute("content", article.metaDesc);
    return () => { el.remove(); if (prev) meta?.setAttribute("content", prev); };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;
  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <article className="mx-auto max-w-3xl px-6 py-14 md:px-10" data-testid={`article-${article.slug}`}>
      <Link to="/blog" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> All guides
      </Link>
      <p className="mt-6 text-xs uppercase tracking-[0.25em] text-accent">{article.tag}</p>
      <h1 className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-4xl">{article.title}</h1>
      <p className="mt-3 text-xs text-muted-foreground">
        {new Date(article.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · {article.readTime} · Zuntria Playbook
      </p>
      <p className="mt-6 text-base text-muted-foreground md:text-lg">{article.intro}</p>

      {article.sections.map((s) => (
        <section key={s.h2} className="mt-10">
          <h2 className="font-display text-lg md:text-xl">{s.h2}</h2>
          {(s.paras || []).map((p) => (
            <p key={p.slice(0, 40)} className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">{p}</p>
          ))}
          {s.list?.length > 0 && (
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground md:text-base">
              {s.list.map((li) => <li key={li.slice(0, 40)}>{li}</li>)}
            </ul>
          )}
        </section>
      ))}

      <div className="mt-12 rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/10 via-card to-card p-8 text-center" data-testid="article-cta">
        <h2 className="font-display text-lg md:text-xl">Let AI find your next client</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Truffl discovers real local leads for your service, scores them, and writes your outreach — free to start.
        </p>
        <Button asChild className="mt-4 h-11 px-6">
          <Link to="/find-clients">Try the AI Client Finder <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>

      {article.faq?.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-lg md:text-xl">FAQ</h2>
          <div className="mt-4 space-y-3">
            {article.faq.map((f) => (
              <details key={f.q} className="rounded-xl border border-border bg-card px-5 py-4">
                <summary className="cursor-pointer list-none text-sm font-medium">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="mt-12">
        <h2 className="font-display text-base">Keep reading</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {others.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`} className="rounded-xl border border-border bg-card p-4 text-sm hover:border-primary/40">
              {a.title}
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
